import React, { useMemo, useState } from "react";
import {
  BankOutlined,
  CheckOutlined,
  DownOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import BottomSheet from "./BottomSheet";

export default function MobileContextSelectors() {
  const {
    selectedCompany,
    setSelectedCompany,
    selectedWarehouse,
    setSelectedWarehouse,
  } = useSelection();
  const { user } = useAuth();
  const [sheet, setSheet] = useState(null);

  const assignedWarehouses = useMemo(
    () => (Array.isArray(user?.assigned_warehouses) ? user.assigned_warehouses : []),
    [user]
  );

  const companies = useMemo(() => {
    const companyMap = new Map();

    assignedWarehouses.forEach((warehouse) => {
      if (warehouse.company_id && !companyMap.has(warehouse.company_id)) {
        companyMap.set(warehouse.company_id, {
          id: warehouse.company_id,
          name: warehouse.company_name || `Entreprise ${warehouse.company_id}`,
          detail: warehouse.company_activity_label || warehouse.company_activity || "",
        });
      }
    });

    return Array.from(companyMap.values());
  }, [assignedWarehouses]);

  const warehouses = useMemo(
    () =>
      selectedCompany
        ? assignedWarehouses.filter(
            (warehouse) => warehouse.company_id === selectedCompany
          )
        : [],
    [assignedWarehouses, selectedCompany]
  );

  const currentCompany =
    companies.find((company) => company.id === selectedCompany) || null;
  const currentWarehouse =
    warehouses.find((warehouse) => warehouse.id === selectedWarehouse) || null;

  const pickCompany = (companyId) => {
    setSelectedCompany(companyId);
    setSelectedWarehouse(null);
    setSheet(null);
  };

  const pickWarehouse = (warehouseId) => {
    setSelectedWarehouse(warehouseId);
    setSheet(null);
  };

  return (
    <div className="ea-ctx" aria-label="Contexte de travail mobile">
      <button
        type="button"
        className="ea-ctxpill"
        onClick={() => setSheet("company")}
      >
        <BankOutlined className="ea-ctxpill__lead" />
        <span className="ea-ctxpill__txt">
          <small>Entreprise</small>
          {currentCompany?.name || "Choisir"}
        </span>
        <DownOutlined className="ea-ctxpill__cv" />
      </button>

      <button
        type="button"
        className="ea-ctxpill"
        disabled={!selectedCompany}
        onClick={() => setSheet("warehouse")}
      >
        <ShopOutlined className="ea-ctxpill__lead" />
        <span className="ea-ctxpill__txt">
          <small>Établissement</small>
          {currentWarehouse?.name || "Choisir"}
        </span>
        <DownOutlined className="ea-ctxpill__cv" />
      </button>

      <BottomSheet
        open={sheet === "company"}
        title="Choisir l'entreprise"
        onClose={() => setSheet(null)}
      >
        {companies.map((company) => (
          <button
            key={company.id}
            type="button"
            className={`ea-opt ${company.id === selectedCompany ? "sel" : ""}`}
            onClick={() => pickCompany(company.id)}
          >
            <span className="ea-opt__ic">
              <BankOutlined />
            </span>
            <span className="ea-opt__body">
              {company.name}
              {company.detail ? <small>{company.detail}</small> : null}
            </span>
            <CheckOutlined className="ea-opt__check" />
          </button>
        ))}
      </BottomSheet>

      <BottomSheet
        open={sheet === "warehouse"}
        title="Choisir l'établissement"
        onClose={() => setSheet(null)}
      >
        {warehouses.map((warehouse) => (
          <button
            key={warehouse.id}
            type="button"
            className={`ea-opt ${warehouse.id === selectedWarehouse ? "sel" : ""}`}
            onClick={() => pickWarehouse(warehouse.id)}
          >
            <span className="ea-opt__ic">
              <ShopOutlined />
            </span>
            <span className="ea-opt__body">
              {warehouse.name}
              {warehouse.city || warehouse.location ? (
                <small>{warehouse.city || warehouse.location}</small>
              ) : null}
            </span>
            <CheckOutlined className="ea-opt__check" />
          </button>
        ))}
      </BottomSheet>
    </div>
  );
}
